import NextImage, { ImageLoaderProps } from 'next/image';
import { twMerge } from 'tailwind-merge';

import gearIcon from '../assets/gearIcon.svg';

import { FishingReportCardButton } from './FishingReportCardButton';
import { ExternalLink } from '../../../components/Clickables/ExternalLink/ExternalLink';
import { FishCatchesIcon } from '../../../components/Icons/FishCatchesIcon';
import { NextImageWrapper } from '../../../components/NextImage/NextImageWrapper';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { isCatchNew } from '@domain/catches/isCatchNew';
import { convertLength } from '@lib/units/convertLength';
import { convertWeight } from '@lib/units/convertWeight';
import { getRelativeTimeAgo } from '@lib/date/getRelativeTimeAgo';
import { useUserInfo } from '@domain/user/useUserInfo';
import { getSpeciesUrl, getFishingWaterUrl } from '@domain/routes/urls';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import ContentLoader from 'react-content-loader';
import { getImgProxyLoader } from '@lib/images/imgProxyLoader';
import { Text } from '../../../components/Text/Text';
import { useState } from 'react';
import { FishingReportDownloadModal } from './FishingReportDownloadModal';

export interface FishingReport {
  _id: string;
  catch: {
    caughtAtGmt: string | null;
    _id: string;
    fishingWater: { _id: string; displayName: string } | null;
    species: { _id: string; displayName: string } | null;
    length: number | null;
    weight: number | null;
  } | null;
  createdAt: any;
  displayProductUnits: { totalCount: number };
  images: {
    edges:
      | ({
          node: {
            alt?: string;
            height: number;
            url: string;
            width: number;
          } | null;
        } | null)[]
      | null;
    totalCount: number;
  };
  user: {
    _id: string;
    nickname: string;
  };
}
interface IProps {
  post: FishingReport;
  className?: string;
  sourceId?: string;
  showFishingWaterName: boolean;
}

export const FishingReportCard = ({ post, className, sourceId, showFishingWaterName }: IProps) => {
  const { currentPage } = useAnalytics();
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);

  const clientHasRendered = useClientHasRendered();
  const image = post.images.edges?.[0]?.node;
  const imageUrl = image?.url ?? '';
  const imageWidth = image?.width ?? 0;
  const imageHeight = image?.height;

  const postCatch = post.catch;
  const species = postCatch?.species;
  const createdAt = post.createdAt;
  const postCommentDate = new Date(createdAt);

  // todo: check new catch logic caughtAtGmt vs createdAt
  const caughtAt = postCatch?.caughtAtGmt;
  const isNewCatch = caughtAt ? isCatchNew(new Date(caughtAt)) : false;

  const usedGearsCount = post.displayProductUnits.totalCount;
  const length = postCatch?.length;
  const weight = postCatch?.weight;
  const { user } = useUserInfo();
  const convertedLength = length ? convertLength(length, user?.lengthUnit) : undefined;
  const convertedWeight = weight ? convertWeight(weight, user?.weightUnit, false, true) : undefined;

  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, imageWidth, imageHeight, { quality });

  const fishingWaterId = postCatch?.fishingWater?._id;
  const fishingWaterName = postCatch?.fishingWater?.displayName;

  const FISHING_REPORT_DATE_KEY = `fb-unique-key-${post._id}`;

  return (
    <div className={twMerge('relative flex flex-col rounded-xl', className)} key={post._id}>
      <FishingReportCardButton
        className="size-full overflow-hidden"
        data-testid={`recent-catch-${post._id}`}
        id={post._id}
        eventProperties={{
          page: currentPage,
          source: 'FishingReportCard',
          source_id: sourceId ?? fishingWaterId,
          target: 'catch',
          target_id: post._id,
        }}
        onClick={() => setIsAppDownloadModalOpen(true)}
        ariaLabel="recent catch in this fishing water"
      >
        {clientHasRendered && isNewCatch && (
          <div
            style={{
              clipPath:
                'polygon(100% 100%,0 100%,999px calc(100% - 999px),calc(100% - 999px) calc(100% - 999px))',
              transform: 'translate(calc((1 - cos(45deg))*100%), -100%) rotate(45deg)',
              transformOrigin: '0% 100%',
              paddingInline: '1lh',
            }}
            className="z-mid bg-clownfish-shade absolute top-0 right-0 w-[60px] p-[2px] text-center text-[8px] leading-[12px] font-bold text-white sm:w-[68px] sm:p-[4px] sm:text-[10px] md:w-[86px] md:text-[12px] md:leading-[16px]"
          >
            NEW
          </div>
        )}
        <NextImageWrapper
          wrapperClassName="size-full pb-[100%]"
          customPlaceholderIcon={<FishCatchesIcon size={100} title="fish catches icon" />}
          imageClassName="aspect-square size-full rounded-t-xl object-cover"
          alt="recently logged catches"
          fill
          src={imageUrl}
          loader={imgProxyLoader}
          sizes="(min-width: 1680px) 385px,(min-width: 600px) 25vw, 50vw"
        />

        <div className="bg-lake-fog flex flex-row-reverse rounded-b-xl px-2.5 py-2 sm:px-3 sm:py-2 md:px-4 md:py-3">
          <div className="size-full">
            {clientHasRendered ? (
              <div className="relative">
                <Text variant="sm" className="m-0 size-full truncate pr-8">
                  {getRelativeTimeAgo(postCommentDate)}
                </Text>
                {!!usedGearsCount && (
                  <NextImage
                    width={32}
                    height={24}
                    className="absolute top-0 right-0 h-6 w-8 overflow-visible object-contain"
                    src={gearIcon}
                    unoptimized
                    alt="Upload preview"
                  />
                )}
              </div>
            ) : (
              <ContentLoader height={24} uniqueKey={FISHING_REPORT_DATE_KEY} />
            )}
            {species ? (
              <Text variant="sm" className="m-0 size-full truncate font-semibold">
                {species.displayName}
              </Text>
            ) : (
              <div className="p-2"></div>
            )}
            {showFishingWaterName &&
              (fishingWaterName ? (
                <Text variant="sm" className="m-0 truncate">
                  {fishingWaterName}
                </Text>
              ) : (
                <div className="p-2"></div>
              ))}
            {convertedLength && convertedWeight ? (
              <Text
                variant="sm"
                className="m-0 truncate"
              >{`${convertedLength} · ${convertedWeight}`}</Text>
            ) : (
              <Text variant="sm" className="text-lake-fog m-0 truncate">
                length · weight
              </Text>
            )}
          </div>
        </div>
      </FishingReportCardButton>
      {/* SEO Section */}
      {species?._id && species.displayName && (
        <ExternalLink href={getSpeciesUrl(species._id, species.displayName)} className="hidden">
          {species.displayName}
        </ExternalLink>
      )}
      {fishingWaterId && fishingWaterName && (
        <ExternalLink
          href={getFishingWaterUrl(fishingWaterId, fishingWaterName)}
          className="hidden"
        >
          {fishingWaterName}
        </ExternalLink>
      )}
      <FishingReportDownloadModal
        imgProxyLoader={imgProxyLoader}
        isAppDownloadModalOpen={isAppDownloadModalOpen}
        setIsAppDownloadModalOpen={setIsAppDownloadModalOpen}
        sourceId={sourceId ?? fishingWaterId}
        imageUrl={imageUrl}
        isNewCatch={isNewCatch}
      />
    </div>
  );
};
